import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaHeart } from 'react-icons/fa';
import { api } from '../services/api';
import ConfirmationModal from './ConfirmationModal';
import { useFeedbackModal } from './common/useFeedbackModal';
import './SymptomChecker.css';

const AVAILABLE_SYMPTOMS = [
  'Morning Sickness',
  'Mild Nausea',
  'Severe Nausea',
  'Fatigue',
  'Mild Headache',
  'Severe Headache',
  'Mild Back Pain',
  'Mild Swelling',
  'Severe Swelling',
  'Mild Cramping',
  'Severe Abdominal Pain',
  'Vaginal Bleeding',
  'Decreased Fetal Movement',
  'No Fetal Movement',
  'Fever',
  'Vision Changes',
  'Shortness of Breath',
];

function SymptomChecker({ userId }) {
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [activeSymptomTab, setActiveSymptomTab] = useState('common');
  const [activeAssessmentTab, setActiveAssessmentTab] = useState(null);
  const [activeHistoryTab, setActiveHistoryTab] = useState('today');
  const { modal, openModal, closeModal } = useFeedbackModal();

  const loadHistory = useCallback(async () => {
    try {
      const data = await api.getSymptomHistory(userId);
      setHistory(data);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadHistory();
    }
  }, [userId, loadHistory]);

  const toggleSymptom = (symptom) => {
    setSelectedSymptoms(prev => {
      if (prev.includes(symptom)) {
        return prev.filter(s => s !== symptom);
      } else {
        return [...prev, symptom];
      }
    });
  };

  const checkSymptoms = async () => {
    if (selectedSymptoms.length === 0) {
      openModal({
        title: 'No symptoms selected',
        message: 'Please select at least one symptom.',
        type: 'warning'
      });
      return;
    }

    try {
      setLoading(true);
      const result = await api.checkSymptoms(userId, selectedSymptoms);
      console.log('Assessment result:', result);
      setAssessment(result);
      // Reset active assessment tab when new assessment comes in
      setActiveAssessmentTab(null);
      loadHistory();
    } catch (error) {
      console.error('Error checking symptoms:', error);
      openModal({
        title: 'Check failed',
        message: 'Failed to check symptoms.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const getAssessmentConfig = (assessment) => {
    const configs = {
      EMERGENCY: { 
        icon: '🚨', 
        color: 'var(--error-color)', 
        bg: 'var(--error-light)',
        label: 'Emergency'
      },
      WARNING: { 
        icon: '⚠️', 
        color: 'var(--warning-color)', 
        bg: 'var(--warning-light)',
        label: 'Warning'
      },
      CAUTION: { 
        icon: '⚡', 
        color: 'var(--info-color)', 
        bg: 'var(--info-light)',
        label: 'Caution'
      },
      NORMAL: { 
        icon: '💖', 
        color: 'var(--success-color)', 
        bg: 'var(--success-light)',
        label: 'Normal'
      }
    };
    return configs[assessment] || configs.NORMAL;
  };

  // Categorize symptoms for tab organization
  const categorizeSymptoms = (symptoms) => {
    const categories = {
      common: [],
      normal: [],
      monitor: [],
      actionNeeded: []
    };

    symptoms.forEach(symptom => {
      const symptomLower = symptom.toLowerCase();
      if (symptomLower.includes('severe') || 
          symptomLower.includes('bleeding') || 
          symptomLower.includes('no movement') ||
          symptomLower.includes('fever') ||
          symptomLower.includes('vision') ||
          symptomLower.includes('shortness')) {
        categories.actionNeeded.push(symptom);
      } else if (symptomLower.includes('morning sickness') || 
                 symptomLower.includes('nausea') ||
                 symptomLower.includes('fatigue') ||
                 symptomLower.includes('headache') ||
                 symptomLower.includes('back pain') ||
                 symptomLower.includes('swelling') ||
                 symptomLower.includes('cramping')) {
        categories.common.push(symptom);
      } else if (symptomLower.includes('mild')) {
        categories.normal.push(symptom);
      } else {
        categories.monitor.push(symptom);
      }
    });

    return categories;
  };

  const symptomCategories = categorizeSymptoms(AVAILABLE_SYMPTOMS);
  const symptomTabs = [
    { key: 'common', label: 'Common', icon: '💝', symptoms: symptomCategories.common },
    { key: 'normal', label: 'Normal', icon: '✅', symptoms: symptomCategories.normal },
    { key: 'monitor', label: 'Monitor', icon: '👁️', symptoms: symptomCategories.monitor },
    { key: 'actionNeeded', label: 'Action Needed', icon: '⚠️', symptoms: symptomCategories.actionNeeded }
  ].filter(tab => tab.symptoms.length > 0);

  // Categorize assessment results for tab organization
  const categorizeAssessments = (assessments) => {
    const categories = {
      emergency: [],
      warning: [],
      caution: [],
      normal: []
    };

    assessments.forEach(item => {
      const assessment = item.assessment || 'NORMAL';
      if (assessment === 'EMERGENCY') {
        categories.emergency.push(item);
      } else if (assessment === 'WARNING') {
        categories.warning.push(item);
      } else if (assessment === 'CAUTION') {
        categories.caution.push(item);
      } else {
        categories.normal.push(item);
      }
    });

    return categories;
  };

  return (
    <div className="symptom-checker-modern">
      <div className="symptom-checker-header-modern">
        <div className="header-content">
          <div className="header-icon-wrapper-bubble">
            <FaHeart className="header-icon-large" />
          </div>
          <div>
            <h2 className="checker-title" style={{ color: '#E91E63' }}>How Are You Feeling Today?</h2>
            <p className="checker-subtitle">Track your symptoms and get personalized care recommendations</p>
          </div>
        </div>
      </div>

      <div className="symptom-selector-modern">
        <div className="selector-info">
          <p className="info-text">
            💝 We're here to help! Select any symptoms you're experiencing, and we'll provide gentle guidance and recommendations tailored just for you.
          </p>
        </div>
        
        {/* Tab-based Symptom Organization - Matching Recommended Meals */}
        <div className="foods-container meal-tabs-container-pink">
          <div className="meal-tabs-wrapper-pink">
            <div className="meal-tabs-pink">
              {symptomTabs.map((tab) => {
                const isActive = activeSymptomTab === tab.key;
                return (
                  <motion.button
                    key={tab.key}
                    className={`meal-tab-pink ${isActive ? 'active' : ''}`}
                    onClick={() => setActiveSymptomTab(tab.key)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="tab-icon-pink">{tab.icon}</span>
                    <span className="tab-label-pink">{tab.label}</span>
                    {tab.symptoms.length > 0 && (
                      <span className="tab-count-pink">{tab.symptoms.length}</span>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Active Tab Symptoms */}
          <div className="meal-content-pink">
            {symptomCategories[activeSymptomTab] && symptomCategories[activeSymptomTab].length > 0 ? (
              <div className="symptoms-grid-modern">
                {symptomCategories[activeSymptomTab].map(symptom => (
                  <motion.button
                    key={symptom}
                    className={`symptom-chip-modern ${selectedSymptoms.includes(symptom) ? 'selected' : ''}`}
                    onClick={() => toggleSymptom(symptom)}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <span className="chip-text">{symptom}</span>
                    {selectedSymptoms.includes(symptom) && (
                      <motion.span 
                        className="chip-check"
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 500, damping: 15 }}
                      >
                        ✓
                      </motion.span>
                    )}
                  </motion.button>
                ))}
              </div>
            ) : (
              <div className="empty-meals">
                <p>No symptoms in this category.</p>
              </div>
            )}
          </div>
        </div>
        
        <motion.button 
          className="btn-modern btn-modern-primary check-symptoms-btn-modern"
          onClick={checkSymptoms}
          disabled={loading || selectedSymptoms.length === 0}
          whileHover={selectedSymptoms.length > 0 ? { scale: 1.02, y: -2 } : {}}
          whileTap={selectedSymptoms.length > 0 ? { scale: 0.98 } : {}}
        >
          {loading ? (
            <>
              <span className="button-spinner"></span>
              <span>Checking...</span>
            </>
          ) : (
            <>
              <span>🔍</span>
              <span>Check Symptoms ({selectedSymptoms.length})</span>
            </>
          )}
        </motion.button>
      </div>

      {assessment && (
        <motion.div 
          className="symptom-assessment-modern"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ display: 'block', visibility: 'visible' }}
        >
          {/* Main Assessment Header - Matching Recommended Meals Design */}
          <div className="subsection-header-creative">
            <div className="subsection-header-with-toggle">
              <div className="subsection-title-wrapper">
                <motion.div
                  animate={{ 
                    scale: assessment.overallAssessment === 'EMERGENCY' ? [1, 1.1, 1] : [1, 1.05, 1],
                    rotate: assessment.overallAssessment === 'EMERGENCY' ? [0, -5, 5, 0] : [0, -3, 3, 0]
                  }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <div className="subsection-icon" style={{ fontSize: '1.8rem', lineHeight: '1' }}>
                    {getAssessmentConfig(assessment.overallAssessment).icon}
                  </div>
                </motion.div>
                <div>
                  <h3 className="subsection-title">Your Assessment: {getAssessmentConfig(assessment.overallAssessment).label}</h3>
                  <p className="subsection-subtitle-small">
                    {assessment.overallAssessment === 'EMERGENCY' && (
                      <>🚨 Immediate medical attention is required. Please contact your healthcare provider or go to the emergency room right away. Your health and your baby's safety are our absolute top priority.</>
                    )}
                    {assessment.overallAssessment === 'WARNING' && (
                      <>⚠️ These symptoms require prompt medical attention. We strongly recommend contacting your healthcare provider within 24 hours to discuss these symptoms and receive appropriate guidance.</>
                    )}
                    {assessment.overallAssessment === 'CAUTION' && (
                      <>👀 Monitor these symptoms closely. If they persist, worsen, or cause concern, please consult your healthcare provider. It's always better to be safe and get professional advice.</>
                    )}
                    {assessment.overallAssessment === 'NORMAL' && (
                      <>💖 These symptoms are typically normal during pregnancy. Continue monitoring your well-being and don't hesitate to reach out to your healthcare provider if anything changes or concerns you.</>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Tab-based Assessment Organization - Matching Recommended Meals */}
          {assessment.assessments && assessment.assessments.length > 0 && (() => {
            const categorized = categorizeAssessments(assessment.assessments);
            const assessmentTabs = [
              { key: 'emergency', label: 'Emergency', icon: '🚨', items: categorized.emergency },
              { key: 'warning', label: 'Warning', icon: '⚠️', items: categorized.warning },
              { key: 'caution', label: 'Caution', icon: '⚡', items: categorized.caution },
              { key: 'normal', label: 'Normal', icon: '💖', items: categorized.normal }
            ].filter(tab => tab.items.length > 0);

            // Set default active tab
            const currentActiveTab = activeAssessmentTab || (assessmentTabs.length > 0 ? assessmentTabs[0].key : null);
            if (currentActiveTab && !activeAssessmentTab && assessmentTabs.length > 0) {
              setTimeout(() => setActiveAssessmentTab(assessmentTabs[0].key), 0);
            }

            const activeTabData = assessmentTabs.find(tab => tab.key === currentActiveTab);

            return (
              <div className="foods-container meal-tabs-container-pink">
                <div className="meal-tabs-wrapper-pink">
                  <div className="meal-tabs-pink">
                    {assessmentTabs.map((tab) => {
                      const isActive = activeAssessmentTab === tab.key;
                      return (
                        <motion.button
                          key={tab.key}
                          className={`meal-tab-pink ${isActive ? 'active' : ''}`}
                          onClick={() => setActiveAssessmentTab(tab.key)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <span className="tab-icon-pink">{tab.icon}</span>
                          <span className="tab-label-pink">{tab.label}</span>
                          {tab.items.length > 0 && (
                            <span className="tab-count-pink">{tab.items.length}</span>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>

                <div className="meal-content-pink">
                  {activeTabData && activeTabData.items.length > 0 ? (
                    <div className="meals-grid-pink">
                      {activeTabData.items.map((item, index) => (
                        <AssessmentItem key={index} item={item} index={index} />
                      ))}
                    </div>
                  ) : (
                    <motion.div
                      key="empty-assessment-state"
                      className="empty-meals"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <p>No assessments in this category.</p>
                    </motion.div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Overall Recommendations - Matching Recommended Meals Design */}
          {assessment.recommendations && assessment.recommendations.length > 0 && (
            <motion.div 
              className="nutrition-subsection recommended-foods-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="subsection-header-creative">
                <div className="subsection-header-with-toggle">
                  <div className="subsection-title-wrapper">
                    <motion.div
                      animate={{ rotate: [0, -10, 10, 0] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <div className="subsection-icon" style={{ fontSize: '1.8rem', lineHeight: '1' }}>
                        💡
                      </div>
                    </motion.div>
                    <div>
                      <h3 className="subsection-title">Personalized Care Tips</h3>
                      <p className="subsection-subtitle-small">
                        Gentle, personalized recommendations to support your well-being and your baby's health
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="foods-container meal-tabs-container-pink">
                <div className="meal-content-pink">
                  <div className="meals-grid-pink">
                    {assessment.recommendations.map((rec, index) => (
                      <RecommendationCard key={index} recommendation={rec} index={index} />
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}

      {history.length > 0 && (
        <motion.div 
          className="nutrition-subsection recommended-foods-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="subsection-header-creative">
            <div className="subsection-header-with-toggle">
              <div className="subsection-title-wrapper">
                <motion.div
                  animate={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <div className="subsection-icon" style={{ fontSize: '1.8rem', lineHeight: '1' }}>
                    📋
                  </div>
                </motion.div>
                <div>
                  <h3 className="subsection-title">Symptom History</h3>
                  <p className="subsection-subtitle-small">
                    Track your symptom assessments over time - {history.length} {history.length === 1 ? 'record' : 'records'} available
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="foods-container meal-tabs-container-pink">
            {/* Date-based Tabs */}
            <div className="meal-tabs-pink">
              {(() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const todayCount = history.filter(log => {
                  const logDate = new Date(log.logDate);
                  logDate.setHours(0, 0, 0, 0);
                  return logDate.getTime() === today.getTime();
                }).length;
                
                const weekAgo = new Date(today);
                weekAgo.setDate(weekAgo.getDate() - 7);
                const thisWeekCount = history.filter(log => {
                  const logDate = new Date(log.logDate);
                  logDate.setHours(0, 0, 0, 0);
                  return logDate >= weekAgo && logDate < today;
                }).length;
                
                const monthAgo = new Date(today);
                monthAgo.setDate(monthAgo.getDate() - 30);
                const thisMonthCount = history.filter(log => {
                  const logDate = new Date(log.logDate);
                  logDate.setHours(0, 0, 0, 0);
                  return logDate >= monthAgo && logDate < weekAgo;
                }).length;
                
                const olderCount = history.filter(log => {
                  const logDate = new Date(log.logDate);
                  logDate.setHours(0, 0, 0, 0);
                  return logDate < monthAgo;
                }).length;
                
                const tabs = [
                  { key: 'today', label: 'Today', count: todayCount, icon: '📅' },
                  { key: 'week', label: 'This Week', count: thisWeekCount, icon: '📆' },
                  { key: 'month', label: 'This Month', count: thisMonthCount, icon: '🗓️' },
                  { key: 'older', label: 'Older', count: olderCount, icon: '📜' }
                ];
                
                return tabs.map(tab => (
                  <button
                    key={tab.key}
                    className={`meal-tab-pink ${activeHistoryTab === tab.key ? 'active' : ''}`}
                    onClick={() => setActiveHistoryTab(tab.key)}
                    disabled={tab.count === 0}
                  >
                    <span className="tab-icon">{tab.icon}</span>
                    <span className="tab-label">{tab.label}</span>
                    {tab.count > 0 && (
                      <span className="tab-count">{tab.count}</span>
                    )}
                  </button>
                ));
              })()}
            </div>
            
            <div className="meal-content-pink">
              <div className="meals-grid-pink">
                {(() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  
                  let filteredHistory = [];
                  
                  if (activeHistoryTab === 'today') {
                    filteredHistory = history.filter(log => {
                      const logDate = new Date(log.logDate);
                      logDate.setHours(0, 0, 0, 0);
                      return logDate.getTime() === today.getTime();
                    });
                  } else if (activeHistoryTab === 'week') {
                    const weekAgo = new Date(today);
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    filteredHistory = history.filter(log => {
                      const logDate = new Date(log.logDate);
                      logDate.setHours(0, 0, 0, 0);
                      return logDate >= weekAgo && logDate < today;
                    });
                  } else if (activeHistoryTab === 'month') {
                    const monthAgo = new Date(today);
                    monthAgo.setDate(monthAgo.getDate() - 30);
                    const weekAgo = new Date(today);
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    filteredHistory = history.filter(log => {
                      const logDate = new Date(log.logDate);
                      logDate.setHours(0, 0, 0, 0);
                      return logDate >= monthAgo && logDate < weekAgo;
                    });
                  } else if (activeHistoryTab === 'older') {
                    const monthAgo = new Date(today);
                    monthAgo.setDate(monthAgo.getDate() - 30);
                    filteredHistory = history.filter(log => {
                      const logDate = new Date(log.logDate);
                      logDate.setHours(0, 0, 0, 0);
                      return logDate < monthAgo;
                    });
                  }
                  
                  // Sort by date (newest first)
                  filteredHistory.sort((a, b) => new Date(b.logDate) - new Date(a.logDate));
                  
                  if (filteredHistory.length === 0) {
                    return (
                      <div className="empty-state" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 'var(--spacing-xl)', color: 'var(--text-secondary)' }}>
                        <p style={{ fontSize: '1.1rem', marginBottom: 'var(--spacing-sm)' }}>No records found</p>
                        <p style={{ fontSize: '0.9rem' }}>You don't have any symptom records for this time period.</p>
                      </div>
                    );
                  }
                  
                  return filteredHistory.map((log, index) => (
                    <HistoryCard key={log.id} log={log} index={index} />
                  ));
                })()}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      <ConfirmationModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={() => {}}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText="OK"
        cancelText=""
      />
    </div>
  );
}

function AssessmentItem({ item, index }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const getBadgeConfig = (assessment) => {
    const configs = {
      EMERGENCY: { color: 'error', icon: '🚨', label: 'Emergency' },
      WARNING: { color: 'warning', icon: '⚠️', label: 'Warning' },
      CAUTION: { color: 'info', icon: '⚡', label: 'Caution' },
      NORMAL: { color: 'success', icon: '💖', label: 'Normal' }
    };
    return configs[assessment] || configs.NORMAL;
  };

  const badgeConfig = getBadgeConfig(item.assessment);
  const preview = item.description ? (item.description.length > 100 ? `${item.description.substring(0, 100)}...` : item.description) : '';

  return (
    <motion.div 
      className={`meal-card-pink symptom-assessment-card ${isExpanded ? 'expanded' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.3 }}
      whileHover={!isExpanded ? { scale: 1.02, y: -4 } : {}}
    >
      <div 
        className="meal-card-header" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="meal-card-main">
          <div className="meal-card-title-section">
            <h4 className="meal-name-modern">{badgeConfig.icon} {item.inputSymptom}</h4>
            {preview && (
              <p className="meal-description-short">{preview}</p>
            )}
          </div>
          <div className="meal-card-meta">
            <div className={`meal-badge calories-badge badge-${badgeConfig.color}`}>
              <span className="badge-icon">{badgeConfig.icon}</span>
              <span className="badge-value">{badgeConfig.label}</span>
            </div>
          </div>
        </div>
        <button className="expand-button" aria-label={isExpanded ? 'Collapse' : 'Expand'}>
          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
        </button>
      </div>

      {isExpanded && (
        <div className="meal-card-details">
          {item.description && (
            <div className="detail-section">
              <h5 className="detail-title">
                <span className="detail-icon">📝</span>
                Description
              </h5>
              <p className="recipe-text">{item.description}</p>
            </div>
          )}
          {item.recommendation && (
            <div className="detail-section">
              <h5 className="detail-title">
                <span className="detail-icon">💝</span>
                Recommendation
              </h5>
              <p className="recipe-text">{item.recommendation}</p>
            </div>
          )}
          {item.sourcedFrom && (
            <div className="meal-note-modern">
              <span className="note-icon">📚</span>
              <span className="note-text">
                Source: {item.sourcedFrom === 'ACOG' ? 'ACOG' : 
                         item.sourcedFrom === 'CDC' ? 'CDC' : 
                         item.sourcedFrom === 'MAYO_CLINIC' ? 'Mayo Clinic' : 
                         item.sourcedFrom === 'WHO' ? 'WHO' : item.sourcedFrom}
                {item.sourceUrl && (
                  <a 
                    href={item.sourceUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{ marginLeft: '8px', color: '#E91E63', textDecoration: 'underline' }}
                  >
                    ↗
                  </a>
                )}
              </span>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

function RecommendationCard({ recommendation, index }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const preview = recommendation.length > 100 ? `${recommendation.substring(0, 100)}...` : recommendation;

  return (
    <motion.div 
      className={`meal-card-pink recommendation-card-pink ${isExpanded ? 'expanded' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.3 }}
      whileHover={!isExpanded ? { scale: 1.02, y: -4 } : {}}
    >
      <div 
        className="meal-card-header" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="meal-card-main">
          <div className="meal-card-title-section">
            <h4 className="meal-name-modern">💡 Tip {index + 1}</h4>
            {preview && (
              <p className="meal-description-short">{preview}</p>
            )}
          </div>
          <div className="meal-card-meta">
            <div className="meal-badge calories-badge">
              <span className="badge-icon">💡</span>
              <span className="badge-value">Tip</span>
            </div>
          </div>
        </div>
        <button className="expand-button" aria-label={isExpanded ? 'Collapse' : 'Expand'}>
          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
        </button>
      </div>

      {isExpanded && (
        <div className="meal-card-details">
          <div className="detail-section">
            <h5 className="detail-title">
              <span className="detail-icon">💝</span>
              Recommendation
            </h5>
            <p className="recipe-text">{recommendation}</p>
          </div>
        </div>
      )}
    </motion.div>
  );
}

function HistoryCard({ log, index }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const getBadgeConfig = (assessment) => {
    const configs = {
      EMERGENCY: { color: 'error', icon: '🚨', label: 'Emergency' },
      WARNING: { color: 'warning', icon: '⚠️', label: 'Warning' },
      CAUTION: { color: 'info', icon: '⚡', label: 'Caution' },
      NORMAL: { color: 'success', icon: '💖', label: 'Normal' }
    };
    return configs[assessment] || configs.NORMAL;
  };

  const badgeConfig = getBadgeConfig(log.assessment);
  const dateFormatted = new Date(log.logDate).toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric',
    year: 'numeric'
  });
  const symptomsPreview = log.symptoms.length > 0 
    ? `${log.symptoms.slice(0, 3).join(', ')}${log.symptoms.length > 3 ? ` +${log.symptoms.length - 3} more` : ''}`
    : 'No symptoms recorded';

  return (
    <motion.div 
      className={`meal-card-pink history-card-pink ${isExpanded ? 'expanded' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.3 }}
      whileHover={!isExpanded ? { scale: 1.02, y: -4 } : {}}
    >
      <div 
        className="meal-card-header" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="meal-card-main">
          <div className="meal-card-title-section">
            <h4 className="meal-name-modern">{dateFormatted}</h4>
            <p className="meal-description-short">{symptomsPreview}</p>
          </div>
          <div className="meal-card-meta">
            <div className={`meal-badge calories-badge badge-${badgeConfig.color}`}>
              <span className="badge-icon">{badgeConfig.icon}</span>
              <span className="badge-value">{badgeConfig.label}</span>
            </div>
          </div>
        </div>
        <button className="expand-button" aria-label={isExpanded ? 'Collapse' : 'Expand'}>
          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
        </button>
      </div>

      {isExpanded && (
        <div className="meal-card-details">
          <div className="detail-section">
            <h5 className="detail-title">
              <span className="detail-icon">📅</span>
              Date
            </h5>
            <p className="recipe-text">{dateFormatted}</p>
          </div>
          
          {log.symptoms && log.symptoms.length > 0 && (
            <div className="detail-section">
              <h5 className="detail-title">
                <span className="detail-icon">💭</span>
                Symptoms Checked
              </h5>
              <div className="recipe-text">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {log.symptoms.map((symptom, idx) => (
                    <li key={idx} style={{ marginBottom: '4px' }}>{symptom}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          <div className="detail-section">
            <h5 className="detail-title">
              <span className="detail-icon">{badgeConfig.icon}</span>
              Assessment
            </h5>
            <p className="recipe-text">
              <strong>{badgeConfig.label}</strong> - {log.assessment}
            </p>
            {log.assessment === 'EMERGENCY' && (
              <p style={{ marginTop: '8px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                This assessment required immediate medical attention. If you experienced this, we hope you received appropriate care.
              </p>
            )}
            {log.assessment === 'WARNING' && (
              <p style={{ marginTop: '8px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                This assessment indicated symptoms that required prompt medical attention.
              </p>
            )}
            {log.assessment === 'CAUTION' && (
              <p style={{ marginTop: '8px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                This assessment suggested monitoring symptoms closely and consulting your healthcare provider if needed.
              </p>
            )}
            {log.assessment === 'NORMAL' && (
              <p style={{ marginTop: '8px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                This assessment indicated that your symptoms were typically normal during pregnancy.
              </p>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default SymptomChecker;
